type KeyAccessor<T> =
    | keyof T
    | ((item: T) => string | number | null | undefined);

function resolveKey<T>(
    item: T,
    key: KeyAccessor<T>,
): string | number | null | undefined {
    return typeof key === "function" ? key(item) : (item[key] as any);
}

export function keyBy<T>(
    arr: readonly T[],
    key: KeyAccessor<T>,
): Record<string, T> {
    const result: Record<string, T> = {};
    for (const item of arr) {
        result[String(resolveKey(item, key))] = item;
    }
    return result;
}

export function groupBy<T>(
    arr: readonly T[] | null | undefined,
    key: KeyAccessor<T>,
): Record<string, T[]> {
    const result: Record<string, T[]> = {};
    if (!arr) {
        return result;
    }
    for (const item of arr) {
        const k = String(resolveKey(item, key));
        if (!result[k]) {
            result[k] = [];
        }
        result[k].push(item);
    }
    return result;
}

type Iteratee<T> = keyof T | ((item: T) => unknown);

// Matches lodash compareAscending: defined values < null < undefined.
function compareValues(a: unknown, b: unknown): number {
    if (a === b) {
        return 0;
    }
    if (a == null || b == null) {
        if (a == null && b == null) {
            return (a === undefined ? 1 : 0) - (b === undefined ? 1 : 0);
        }
        return a == null ? 1 : -1;
    }
    const aComp = a as string | number;
    const bComp = b as string | number;
    if (aComp < bComp) {
        return -1;
    }
    if (aComp > bComp) {
        return 1;
    }
    return 0;
}

export function sortBy<T>(
    arr: readonly T[] | null | undefined,
    iteratees: Array<Iteratee<T>>,
): T[];
export function sortBy<T>(
    arr: readonly T[] | null | undefined,
    ...iteratees: Array<Iteratee<T>>
): T[];
export function sortBy<T>(
    arr: readonly T[] | null | undefined,
    ...args: any[]
): T[] {
    if (!arr) {
        return [];
    }
    const iteratees: Array<Iteratee<T>> = Array.isArray(args[0])
        ? args[0]
        : args;
    return [...arr].sort((a, b) => {
        for (const iteratee of iteratees) {
            const aVal =
                typeof iteratee === "function" ? iteratee(a) : a[iteratee];
            const bVal =
                typeof iteratee === "function" ? iteratee(b) : b[iteratee];
            const cmp = compareValues(aVal, bVal);
            if (cmp !== 0) {
                return cmp;
            }
        }
        return 0;
    });
}

export function orderBy<T>(
    arr: readonly T[] | null | undefined,
    keys:
        | keyof T
        | ((item: T) => unknown)
        | Array<keyof T | ((item: T) => unknown)>,
    orders: "asc" | "desc" | Array<"asc" | "desc">,
): T[] {
    if (!arr) {
        return [];
    }
    const keyArr = Array.isArray(keys) ? keys : [keys];
    const orderArr = Array.isArray(orders) ? orders : [orders];
    return [...arr].sort((a, b) => {
        for (let i = 0; i < keyArr.length; i++) {
            const key = keyArr[i];
            const aVal = typeof key === "function" ? key(a) : a[key];
            const bVal = typeof key === "function" ? key(b) : b[key];
            const dir = orderArr[i] === "desc" ? -1 : 1;
            const cmp = compareValues(aVal, bVal);
            if (cmp !== 0) {
                return cmp * dir;
            }
        }
        return 0;
    });
}

export function partition<T>(
    arr: readonly T[] | null | undefined,
    predicate: (item: T) => unknown,
): [T[], T[]] {
    if (!arr) {
        return [[], []];
    }
    const pass: T[] = [];
    const fail: T[] = [];
    for (const item of arr) {
        (predicate(item) ? pass : fail).push(item);
    }
    return [pass, fail];
}

export function chunk<T>(arr: readonly T[], size: number): T[][] {
    if (size <= 0) {
        return [];
    }
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
    }
    return result;
}

export function uniqBy<T>(
    arr: readonly T[],
    key: keyof T | ((item: T) => unknown),
): T[] {
    const seen = new Set<unknown>();
    return arr.filter((item) => {
        const k = typeof key === "function" ? key(item) : item[key];
        if (seen.has(k)) {
            return false;
        }
        seen.add(k);
        return true;
    });
}

export function uniq<T>(arr: readonly T[]): T[] {
    return [...new Set(arr)];
}

export function without<T>(arr: readonly T[], ...values: T[]): T[] {
    const set = new Set(values);
    return arr.filter((item) => !set.has(item));
}

export function difference<T>(
    arr: readonly T[],
    ...others: readonly T[][]
): T[] {
    const excluded = new Set(others.flat());
    return arr.filter((item) => !excluded.has(item));
}

export function range(start: number, end?: number, step?: number): number[] {
    if (end === undefined) {
        end = start;
        start = 0;
    }
    const s = step ?? (start <= end ? 1 : -1);
    if (s === 0) {
        return [];
    }
    const result: number[] = [];
    if (s > 0) {
        for (let i = start; i < end; i += s) {
            result.push(i);
        }
    } else {
        for (let i = start; i > end; i += s) {
            result.push(i);
        }
    }
    return result;
}

export function times<T>(n: number, fn: (index: number) => T): T[] {
    return Array.from({ length: n }, (_, i) => fn(i));
}

export function last<T>(arr: readonly T[] | null | undefined): T | undefined {
    return arr?.at(-1);
}

type Orderable = number | string | Date;

export function maxBy<T>(
    arr: readonly T[] | null | undefined,
    fn: (item: T) => Orderable,
): T | undefined {
    if (!arr || arr.length === 0) {
        return undefined;
    }
    let best = arr[0];
    let bestVal = fn(best);
    for (let i = 1; i < arr.length; i++) {
        const val = fn(arr[i]);
        if (val > bestVal) {
            best = arr[i];
            bestVal = val;
        }
    }
    return best;
}

export function minBy<T>(
    arr: readonly T[] | null | undefined,
    fn: (item: T) => Orderable,
): T | undefined {
    if (!arr || arr.length === 0) {
        return undefined;
    }
    let best = arr[0];
    let bestVal = fn(best);
    for (let i = 1; i < arr.length; i++) {
        const val = fn(arr[i]);
        if (val < bestVal) {
            best = arr[i];
            bestVal = val;
        }
    }
    return best;
}

export function clamp(value: number, lower: number, upper: number): number {
    return Math.min(Math.max(value, lower), upper);
}

export function intersection<T>(...arrays: ReadonlyArray<readonly T[]>): T[] {
    if (arrays.length === 0) {
        return [];
    }
    const [first, ...rest] = arrays;
    const sets = rest.map((arr) => new Set(arr));
    return first.filter((item) => sets.every((s) => s.has(item)));
}

export function flatten<T>(arr: ReadonlyArray<T | readonly T[]>): T[] {
    return arr.flat() as T[];
}

/**
 * Returns true for null/undefined, empty string, empty array, empty Map/Set, and empty object.
 * NOTE: Unlike lodash isEmpty, returns false for numbers, booleans, and functions.
 */
export function isEmpty(value: unknown): boolean {
    if (value == null) {
        return true;
    }
    if (typeof value === "string") {
        return value.length === 0;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (value instanceof Map || value instanceof Set) {
        return value.size === 0;
    }
    if (typeof value === "object") {
        return Object.keys(value as object).length === 0;
    }
    return false;
}

export function size(value: unknown): number {
    if (value == null) {
        return 0;
    }
    if (typeof value === "string") {
        return (value as string).length;
    }
    if (Array.isArray(value)) {
        return value.length;
    }
    if (value instanceof Map || value instanceof Set) {
        return value.size;
    }
    if (typeof value === "object") {
        return Object.keys(value as object).length;
    }
    return 0;
}

export function sum(arr: readonly number[]): number {
    return arr.reduce((acc, n) => acc + n, 0);
}
